/**
 * Palette and copy for the pricing screens.
 *
 * The redesign spec gives exact hex values, and they are a dark palette. This
 * app also ships a light theme, so the spec values are used verbatim in dark
 * and mapped onto the existing theme tokens in light. Anything hard-coded to
 * the spec hexes would render as dark-on-dark for a light-mode user.
 *
 * Copy lives here too, as single strings referenced by both screens. The word
 * "flagged" appears in four places across two screens and has to say the same
 * thing in all of them.
 */

export const COPY = {
  flaggedTitle: 'What flagged means',
  flaggedBody:
    "eBay titles don't always match what actually sold, and some sales get "
    + 'returned. We flag those sales so you can decide whether they count.',
  ebayDrop:
    'eBay removes some sold listings after about a month, so older rows may not open.',
  noSales: 'No recent sales',
  attributionGraded: 'eBay sold data provided by Poketrace',
  attributionRaw: 'eBay sold data provided by Poketrace · TCGplayer by JustTCG',
};

export function palette(theme, isDark = true) {
  if (!isDark) {
    return {
      screen: theme.bg,
      card: theme.bgCard,
      lifted: theme.bgSheet,
      tile: theme.bgSheet,
      popover: theme.bgCard,
      sheet: theme.bgCard,
      input: theme.bgInput,

      hairline: theme.border,
      divider: theme.borderSubtle,
      chipBorder: theme.border,
      liftedBorder: theme.borderHover,

      blue: theme.accent,
      link: theme.accent,
      blueTint: theme.accentBg,
      green: theme.success,
      greenTint: theme.successBg,
      holo: theme.warningDark,
      red: theme.error,
      redTint: theme.errorBg,

      white: theme.text,
      primary: theme.text,
      secondary: theme.textSecondary,
      muted: theme.textMuted,
      faint: theme.textDisabled,
    };
  }

  return {
    screen: '#0b0c0e',
    card: '#131418',
    lifted: '#191c21',
    tile: '#191b1f',
    popover: '#1d2025',
    sheet: '#101114',
    input: '#1a1b1f',

    hairline: '#24262b',
    divider: '#1f2126',
    chipBorder: '#2e3138',
    liftedBorder: '#2a2f38',

    blue: '#1f9bf0',
    link: '#4ba3e3',
    blueTint: '#0e1b26',
    green: '#4ade80',
    greenTint: '#10241a',
    holo: '#f5c542',
    red: '#e07070',
    redTint: '#2a1416',

    white: '#ffffff',
    primary: '#d5d8dc',
    secondary: '#8b9098',
    muted: '#6b7078',
    faint: '#565b62',
  };
}

/** Two decimals always, so a column of prices scans down evenly. */
export const money2 = (n) => (n == null ? null : `$${Number(n).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`);

/** Short relative age for a sale row: "18d ago", "3mo ago". */
export function relativeAge(date, now = Date.now()) {
  const then = Date.parse(date);
  if (!Number.isFinite(then)) return null;
  const days = Math.max(0, Math.floor((now - then) / 86400000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default { COPY, palette, money2, relativeAge };
